import { useState, type CSSProperties, type ReactNode } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { providerFullDisplayName, type LocalizedString } from "@pontimed/types";
import { useSession } from "@pontimed/auth-client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  LogIn,
  PencilLine,
  RotateCw,
  ShieldCheck,
  Trash2,
  UserRound,
  type LucideIcon,
} from "lucide-react";

import { useAuthModal } from "@/components/auth/auth-modal-context";
import { apiErrorCode } from "@/lib/api-error-message";
import { Button, buttonVariants } from "@/components/ui/button";
import { ConfirmSheet } from "@/components/ui/confirm-sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useNoIndexMeta } from "@/lib/hooks/use-noindex-meta";
import { showToast } from "@/lib/ui/toast";
import { tLocalized } from "@/types/localized-string";

const SUPPORT_EMAIL = "support@pontimed.app";

type ListingStatus = "listed" | "removed" | "claimed";

interface ListingNotice {
  provider: {
    firstName: string | null;
    lastName: string | null;
    city: string | null;
    /** Plain label, or a LocalizedString when the API sends the JSONB as is. */
    specialty: string | LocalizedString | null;
  };
  status: ListingStatus;
  profileUrl: string | null;
  claimUrl: string | null;
}

interface ListingRemoveSearch {
  token?: string;
}

/**
 * GDPR art. 14 landing — the one-time information e-mail we send to doctors
 * imported from public registries links here (`/listing/remove?token=…`).
 * Root-level, outside auth: the token is the credential. The doctor can
 * remove the public profile (POST) or go correct the data by claiming it.
 *
 * API (apps/api `routes/listing-notice.ts`):
 *   GET  /api/listing-notice/:token          404 INVALID_TOKEN · 410 TOKEN_EXPIRED
 *   POST /api/listing-notice/:token/remove   409 CLAIMED
 */
export const Route = createFileRoute("/listing/remove")({
  validateSearch: (search: Record<string, unknown>): ListingRemoveSearch => ({
    token: typeof search.token === "string" && search.token ? search.token : undefined,
  }),
  component: ListingRemovePage,
});

function ListingRemovePage() {
  const { token } = Route.useSearch();

  useNoIndexMeta();

  return (
    <main className="relative isolate flex min-h-dvh justify-center px-4 py-8 sm:items-center sm:py-14">
      <PageBackdrop />
      <div className={PAGE_CARD} style={CARD_WASH}>
        {token ? (
          <ListingNoticeBody token={token} />
        ) : (
          <StatusScreen
            icon={AlertCircle}
            tone="destructive"
            title={<Trans>Link not valid</Trans>}
            body={<Trans>Open the link straight from the e-mail we sent you.</Trans>}
            support
          />
        )}
      </div>
    </main>
  );
}

function ListingNoticeBody({ token }: { token: string }) {
  const { t, i18n } = useLingui();
  const queryClient = useQueryClient();
  const queryKey = ["listing-notice", token] as const;

  const query = useQuery<ListingNotice, Error>({
    queryKey,
    queryFn: () =>
      apiFetch.get<ListingNotice>(`/api/listing-notice/${encodeURIComponent(token)}`, {
        skipAuthRefresh: true,
      }),
    retry: (failureCount, error) =>
      !(error instanceof ApiError && (error.status === 404 || error.status === 410)) &&
      failureCount < 2,
    staleTime: Infinity,
  });

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const setStatus = (status: ListingStatus) =>
    queryClient.setQueryData<ListingNotice>(queryKey, (prev) =>
      prev ? { ...prev, status } : prev,
    );

  const remove = async () => {
    setSubmitting(true);
    setSubmitError(null);
    const toast = showToast(t`Removing your profile…`, "loading");
    try {
      await apiFetch.post<{ ok: true; status: "removed" }>(
        `/api/listing-notice/${encodeURIComponent(token)}/remove`,
        undefined,
        { skipAuthRefresh: true },
      );
      setConfirmOpen(false);
      setStatus("removed");
      toast.update(t`Your profile was removed`, "success");
    } catch (err) {
      if (apiErrorCode(err) === "CLAIMED" || (err instanceof ApiError && err.status === 409)) {
        setConfirmOpen(false);
        setStatus("claimed");
        toast.update(t`This profile is managed from a PontiMed account`, "info");
        return;
      }
      const message =
        err instanceof ApiError && (err.status === 404 || err.status === 410)
          ? t`This link is no longer valid. Open the most recent e-mail we sent you.`
          : t`Could not remove your profile. Please try again.`;
      setSubmitError(message);
      setConfirmOpen(false);
      toast.update(message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (query.isLoading) return <LandingSkeleton />;

  if (query.isError || !query.data) {
    const status = query.error instanceof ApiError ? query.error.status : 0;
    if (status === 404) {
      return (
        <StatusScreen
          icon={AlertCircle}
          tone="destructive"
          title={<Trans>Link not found</Trans>}
          body={
            <Trans>The link may be incomplete. Open it again from the e-mail we sent you.</Trans>
          }
          support
        />
      );
    }
    if (status === 410) {
      return (
        <StatusScreen
          icon={AlertCircle}
          tone="destructive"
          title={<Trans>This link has expired</Trans>}
          body={
            <Trans>
              For your security, this link is no longer active. Write to us and we'll remove your
              profile or send you a new link.
            </Trans>
          }
          support
        />
      );
    }
    return (
      <StatusScreen
        icon={AlertCircle}
        tone="destructive"
        title={<Trans>Something went wrong</Trans>}
        body={<Trans>Please check your connection and try again.</Trans>}
        action={
          <Button
            variant="outline"
            className={BTN_SECONDARY}
            disabled={query.isFetching}
            onClick={() => void query.refetch()}
          >
            <RotateCw className={cn("size-4", query.isFetching && "animate-spin")} aria-hidden />
            <Trans>Try again</Trans>
          </Button>
        }
      />
    );
  }

  const data = query.data;
  const name = providerFullDisplayName(data.provider, i18n.locale);
  const specialty =
    typeof data.provider.specialty === "string"
      ? data.provider.specialty
      : tLocalized(data.provider.specialty, i18n.locale);
  const subtitle = [specialty, data.provider.city].filter(Boolean).join(" · ");

  if (data.status === "removed") {
    return (
      <StatusScreen
        icon={CheckCircle2}
        tone="success"
        title={<Trans>Your profile has been removed</Trans>}
        body={
          <Trans>
            The public profile of {name} is no longer shown on PontiMed. We keep only a minimal
            record, so the profile is not imported again, and we won't contact you again.
          </Trans>
        }
      />
    );
  }

  if (data.status === "claimed") {
    return <ClaimedScreen name={name} subtitle={subtitle} />;
  }

  return (
    <div className="space-y-5">
      <ProviderHeader name={name} subtitle={subtitle} />

      <p
        className="puls-reveal text-muted-foreground m-0 text-sm leading-relaxed"
        style={STAGGER_1}
      >
        <Trans>
          We built this profile from public registries so patients can find you. You decide what
          happens to it.
        </Trans>
      </p>

      {submitError && (
        <p className={ERROR_BOX} role="alert">
          {submitError}
        </p>
      )}

      <div className="space-y-3">
        <Button
          className={BTN_PRIMARY}
          disabled={submitting}
          onClick={() => {
            setSubmitError(null);
            setConfirmOpen(true);
          }}
        >
          <Trash2 className="size-4" aria-hidden />
          <Trans>Remove my profile from PontiMed</Trans>
        </Button>

        {data.claimUrl && (
          <a
            href={data.claimUrl}
            className={cn(buttonVariants({ variant: "outline" }), BTN_SECONDARY)}
          >
            <PencilLine className="size-4" aria-hidden />
            <Trans>Correct my data instead</Trans>
          </a>
        )}

        {data.profileUrl && (
          <a
            href={data.profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary inline-flex min-h-11 w-full items-center justify-center gap-1.5 text-sm font-medium underline-offset-4 hover:underline"
          >
            <Trans>View profile</Trans>
            <ExternalLink className="size-3.5" aria-hidden />
          </a>
        )}
      </div>

      <ConfirmSheet
        open={confirmOpen}
        onClose={() => {
          if (!submitting) setConfirmOpen(false);
        }}
        onConfirm={() => void remove()}
        pending={submitting}
        tone="destructive"
        icon={<Trash2 className="size-4" aria-hidden />}
        title={<Trans>Remove your profile?</Trans>}
        description={<Trans>This takes effect right away.</Trans>}
        confirmLabel={<Trans>Remove my profile</Trans>}
        ariaLabel={t`Remove your profile?`}
      >
        <ul className="text-muted-foreground m-0 list-disc space-y-2 pl-5 text-sm leading-relaxed">
          <li>
            <Trans>Your public profile disappears from PontiMed and from our search.</Trans>
          </li>
          <li>
            <Trans>We keep only a minimal record, so the profile is not imported again.</Trans>
          </li>
          <li>
            <Trans>We won't contact you again.</Trans>
          </li>
        </ul>
      </ConfirmSheet>
    </div>
  );
}

function ClaimedScreen({ name, subtitle }: { name: string; subtitle: string }) {
  const { status } = useSession();
  const auth = useAuthModal();
  const signedIn = status === "authenticated";

  return (
    <div className="space-y-5">
      <ProviderHeader name={name} subtitle={subtitle} />
      <div className="flex items-start gap-3">
        <StatusTile icon={ShieldCheck} tone="primary" />
        <div className="min-w-0 space-y-1">
          <h2 className="font-display text-foreground m-0 text-lg leading-tight font-semibold">
            <Trans>This profile is managed from an account</Trans>
          </h2>
          <p className="text-muted-foreground m-0 text-sm leading-relaxed">
            <Trans>
              This profile has been claimed on PontiMed, so it is managed by its owner. Sign in to
              change or remove it from your account.
            </Trans>
          </p>
        </div>
      </div>
      {signedIn ? (
        <a href="/account" className={cn(buttonVariants(), BTN_PRIMARY)}>
          <UserRound className="size-4" aria-hidden />
          <Trans>Open my account</Trans>
        </a>
      ) : (
        <Button className={BTN_PRIMARY} onClick={() => auth.open({ returnTo: "/account" })}>
          <LogIn className="size-4" aria-hidden />
          <Trans>Sign in</Trans>
        </Button>
      )}
    </div>
  );
}

/* ─── presentational ───
   Same Puls status language as `routes/requests/opt-out.tsx` and
   `routes/requests/$token.tsx`: blueprint grid + soft glow behind one card. */

function ProviderHeader({ name, subtitle }: { name: string; subtitle: string }) {
  return (
    <header className="puls-reveal flex items-center gap-4">
      <span
        aria-hidden="true"
        className="border-primary/20 bg-primary-soft text-primary grid size-14 shrink-0 place-items-center rounded-full border"
      >
        <UserRound className="size-6" />
      </span>
      <div className="min-w-0">
        <h1 className="font-display text-foreground m-0 text-xl leading-tight font-semibold tracking-[-0.02em] break-words sm:text-2xl">
          {name}
        </h1>
        {subtitle && <p className="text-muted-foreground m-0 mt-1 text-sm">{subtitle}</p>}
      </div>
    </header>
  );
}

function StatusScreen({
  icon,
  tone,
  title,
  body,
  action,
  support = false,
}: {
  icon: LucideIcon;
  tone: StatusTone;
  title: ReactNode;
  body: ReactNode;
  action?: ReactNode;
  support?: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-4 py-4 text-center">
      <StatusTile icon={icon} tone={tone} />
      <h1 className={TITLE} style={STAGGER_1}>
        {title}
      </h1>
      <p
        className="puls-reveal text-muted-foreground m-0 max-w-sm text-sm leading-relaxed"
        style={STAGGER_2}
      >
        {body}
      </p>
      {action && <div className="w-full">{action}</div>}
      {support && (
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="text-primary inline-flex min-h-11 items-center font-mono text-sm font-medium underline-offset-4 hover:underline"
        >
          {SUPPORT_EMAIL}
        </a>
      )}
    </div>
  );
}

function LandingSkeleton() {
  return (
    <div className="space-y-5" aria-hidden>
      <div className="flex items-center gap-4">
        <Skeleton className="size-14 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-5 w-44" />
          <Skeleton className="h-3 w-28" />
        </div>
      </div>
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-12 w-full rounded-lg" />
      <Skeleton className="h-12 w-full rounded-lg" />
    </div>
  );
}

const GRID_LINE = "color-mix(in oklch, var(--primary) 8%, transparent)";
const GRID_MASK = "radial-gradient(ellipse 70% 60% at 50% 40%, black 15%, transparent 72%)";
const GRID_STYLE: CSSProperties = {
  backgroundImage: `linear-gradient(${GRID_LINE} 1px, transparent 1px), linear-gradient(90deg, ${GRID_LINE} 1px, transparent 1px)`,
  backgroundSize: "44px 44px",
  maskImage: GRID_MASK,
  WebkitMaskImage: GRID_MASK,
};
const GLOW_STYLE: CSSProperties = {
  backgroundImage:
    "radial-gradient(closest-side, color-mix(in oklch, var(--primary) 16%, transparent), color-mix(in oklch, var(--accent) 7%, transparent) 55%, transparent)",
};
/** Soft primary wash at the top of the card. */
const CARD_WASH: CSSProperties = {
  backgroundImage:
    "linear-gradient(180deg, color-mix(in oklch, var(--primary-soft) 55%, var(--card)) 0%, var(--card) 40%)",
};
const PAGE_CARD =
  "border-border bg-card w-full max-w-md self-start rounded-2xl border p-5 shadow-[0_30px_70px_-45px_color-mix(in_oklch,var(--primary)_60%,transparent)] sm:self-auto sm:p-8";
const TITLE =
  "puls-reveal font-display text-foreground m-0 text-2xl leading-tight font-semibold tracking-[-0.025em] text-balance";
const STAGGER_1 = { "--puls-i": 1 } as CSSProperties;
const STAGGER_2 = { "--puls-i": 2 } as CSSProperties;
const BTN = "h-12 w-full gap-2 rounded-lg px-6 text-sm font-semibold";
const BTN_PRIMARY = `${BTN} hover:bg-primary-hover`;
const BTN_SECONDARY = `${BTN} bg-card text-foreground hover:border-primary/40 hover:bg-card hover:text-primary`;
const ERROR_BOX =
  "border-destructive-border bg-destructive-soft text-destructive-strong m-0 rounded-lg border px-3 py-2.5 text-sm";

type StatusTone = "primary" | "success" | "destructive";

const TILE_TONE: Record<StatusTone, string> = {
  primary: "border-primary/20 bg-primary-soft text-primary",
  success: "border-success-border bg-success-soft text-success",
  destructive: "border-destructive-border bg-destructive-soft text-destructive",
};

function PageBackdrop() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div className="absolute inset-0" style={GRID_STYLE} />
      <div
        className="absolute top-[10%] left-1/2 h-[70%] w-[min(92%,44rem)] -translate-x-1/2 blur-[20px]"
        style={GLOW_STYLE}
      />
    </div>
  );
}

function StatusTile({ icon: Icon, tone }: { icon: LucideIcon; tone: StatusTone }) {
  return (
    <span
      aria-hidden="true"
      className={`puls-reveal grid size-14 shrink-0 place-items-center rounded-2xl border ${TILE_TONE[tone]}`}
    >
      <Icon className="size-6" />
    </span>
  );
}
